import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useFormatter } from 'next-intl';
import StyledEventList from './eventList.style';
import EventTeaser from '../../molecules/event-teaser/eventTeaser';
import Headline from '../../atoms/headline/headline';
import Copy from '../../atoms/copy/copy';
import { LongArrowRight } from '../../icons/arrow/arrow';
import Teaser from './components/teaser/teaser';

function stripHtmlTags(text) {
  if (!text) return '';
  return text.replace(/(<([^>]+)>)/gi, '');
}

function Accordion({ index, date, eventListDate }) {
  const [openEvents, setOpenEvents] = useState((index === 0 && date) || null);

  const format = useFormatter();

  const handleClick = item => {
    setOpenEvents(openEvents === item ? '' : item);
  };

  return (
    <div key={date}>
      <div className="dateLine">
        <div className="dateLine__date">{format.dateTime(new Date(date), { day: '2-digit' })}</div>
        <div className="dateLine__line">
          <Headline tag="span" subline>
            {format.dateTime(new Date(date), { day: 'numeric', weekday: 'long', month: 'long' })}
          </Headline>
          <button
            type="button"
            onClick={() => handleClick(date)}
            className={`dateLine__toggle ${openEvents === date && 'dateLine__toggle--active'}`}
          >
            <svg
              className="dateLine__icon"
              width="18"
              height="8"
              viewBox="0 0 18 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M17.2344 1.72966C17.3698 1.60192 17.3698 1.37211 17.2344 1.21902L16.1234 0.198071C15.988 0.044979 15.7441 0.044979 15.5811 0.198071L10.8665 4.66477C10.4338 5.17768 8.21106 5.7866 7.23541 4.66477L2.49362 0.198071C2.35824 0.0703253 2.11432 0.0703253 1.95172 0.198071L0.867899 1.21902C0.70529 1.37211 0.70529 1.60192 0.867899 1.75501L6.82889 7.37074C7.20855 7.70227 7.69638 7.90673 8.21106 7.90673H9.86401C10.3787 7.90673 10.8665 7.70227 11.2458 7.37074L17.2344 1.72966Z"
                fill="black"
              />
            </svg>
            <span className="dateLine__stretched" />
          </button>
        </div>
      </div>
      {openEvents === date && (
        <>
          {eventListDate.map(event => {
            return (
              <div key={event.gomusId}>
                <Teaser
                  bookable={event.gomusDate.seatsAvailable > 0}
                  openForAll={event.openForAll}
                  headline={event.headline}
                  description={
                    stripHtmlTags(event.introCopy || event.description).length > 140
                      ? `${stripHtmlTags(event.introCopy || event.description).substring(
                          0,
                          140
                        )}...`
                      : stripHtmlTags(event.introCopy || event.description)
                  }
                  startTime={event.gomusDate.startTime}
                  duration={event.gomusDate.duration}
                  image={event.teaserImage || null}
                  pageTags={event.eventFilterCategories || null}
                  uri={event.gomusDate.uri}
                />
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

function EventList({
  headline,
  copy,
  linkToAllEventsText,
  linkToAllEventsLink,
  boxFrom,
  boxTo,
  boxHeadline,
  allDay = false,
  boxCopy,
  boxButtonText,
  boxButtonLink,
  eventList
}) {
  return (
    <StyledEventList>
      <div className="event-list__hero-container">
        <div>
          {headline && <Headline secondary>{headline}</Headline>}
          <div className="event-list__topic-copy">
            <Copy>{copy}</Copy>
            {linkToAllEventsText && (
              <a className="event-list__topic-link" href={linkToAllEventsLink}>
                {linkToAllEventsText}
                <LongArrowRight role="presentation" />
              </a>
            )}
          </div>
        </div>
        <div>
          {boxCopy && (
            <EventTeaser
              id={null}
              timeOnTop
              allDay={allDay}
              headline={boxHeadline}
              startDate={boxFrom}
              endDate={boxTo}
              button={boxButtonText}
              uri={boxButtonLink}
              className="event-list__teaser"
              copy={boxCopy}
            />
          )}
        </div>
      </div>
      <div className="event-list__event-container">
        {Object.keys(eventList)
          .sort()
          .map((date, index) => {
            return (
              <Accordion key={index} index={index} date={date} eventListDate={eventList[date]} />
            );
          })}
      </div>
    </StyledEventList>
  );
}

Accordion.propTypes = {
  index: PropTypes.number.isRequired,
  date: PropTypes.string.isRequired,
  eventListDate: PropTypes.arrayOf(
    PropTypes.shape({
      bookable: PropTypes.bool,
      headline: PropTypes.string,
      description: PropTypes.string,
      uri: PropTypes.string,
      teaserImage: PropTypes.shape({
        src: PropTypes.string,
        title: PropTypes.string,
        height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      }),
      gomusEventCategory: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          title: PropTypes.string
        })
      ),
      gomusDate: PropTypes.shape({
        duration: PropTypes.number,
        startTime: PropTypes.string
      })
    })
  ).isRequired
};

EventList.propTypes = {
  headline: PropTypes.string.isRequired,
  copy: PropTypes.string.isRequired,
  linkToAllEventsText: PropTypes.string,
  linkToAllEventsLink: PropTypes.string,
  boxFrom: PropTypes.shape({
    d: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    m: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    y: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    H: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    i: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    c: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  boxTo: PropTypes.shape({
    d: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    m: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    y: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    H: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    i: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    c: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  allDay: PropTypes.bool,
  boxHeadline: PropTypes.string,
  boxCopy: PropTypes.string,
  boxButtonText: PropTypes.string,
  boxButtonLink: PropTypes.string,
  eventList: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        bookable: PropTypes.bool,
        headline: PropTypes.string,
        description: PropTypes.string,
        uri: PropTypes.string,
        teaserImage: PropTypes.shape({
          src: PropTypes.string,
          title: PropTypes.string,
          height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        }),
        eventFilterCategories: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number,
            title: PropTypes.string
          })
        ),
        gomusDate: PropTypes.shape({
          duration: PropTypes.number,
          startTime: PropTypes.string
        })
      })
    )
  ).isRequired
};

export default EventList;
